import React, { useMemo } from 'react';
import { Images } from '../../../@resources/img';
import { LFC } from '../../../Legacy';
import { Matrix } from '../../../platform/geom/Matrix';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RenderTextField } from '../../canvas/visualization/RenderField';
import { RenderBounds, RenderBox, RenderInner, usePose, usePressable } from '../../canvas/visualization/RenderHelpers';
import { CardPile } from './CardPile';
import { ValueCard } from './ValueCard';

const RenderButton = React.memo<{ area: Rect, interaction: string, label: string }>(({ area, interaction, label }) => {

    const transform =
        (interaction === 'idle' ? Matrix.translate(0, -2).toCSS() : null) ??
        (interaction === 'hover' ? Matrix.translate(0, -4).toCSS() : null) ??
        (interaction === 'active' ? Matrix.translate(0, 0).toCSS() : null);

    const backStyle = {
        ...area.toCSS(),
        background: Colors.gray300,
        borderRadius: '8px',
    };

    const foreStyle = {
        ...area.toCSS(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 5px',
        fontFamily: 'Rubik',
        fontSize: Math.round(area.height / 3.5),
        fontWeight: 500,
        fontColor: Colors.text,
        textAlign: 'center',
        lineHeight: '1.2em',
        background: Colors.gray200,
        borderRadius: '8px',
        transform,
        transition: 'transform 75ms ease-out',
    };

    return <>
        <RenderBox {...backStyle} />
        <RenderBox {...foreStyle}>
            {label}
        </RenderBox>
    </>;
});

const RenderBorder: LFC<{ object: CardPile }> = ({ object }) => {
    const { base } = object.layout;
    return <>
        <RenderBox
            {...base.toCSS()}
            background={Colors.white}
            borderRadius="16px"
            border={`solid 1px ${Colors.border}`}
            borderBottom={`solid 3px ${Colors.border}`}
        />
    </>;
};

const RenderStack: LFC<{ object: CardPile }> = ({ object }) => {

    const { stack } = object.layout;

    return <RenderBox
        {...stack.toCSS()}
        background={`${Colors.gray200} url(${Images.LogoWhite}) no-repeat center center`}
        backgroundSize="48px 48px"
        borderRadius="4px"
        border={`solid 3px ${Colors.gray300}`}
    />;
};

const RenderCount: LFC<{ object: CardPile }> = ({ object }) => {

    const { count } = object.layout;
    const value = useMemo(() => object.polarize(ValueCard).length, [object.children]);

    const style = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontFamily: 'Rubik',
        color: Colors.textLight,
        textAlign: 'center',
    };

    return <RenderBox {...count.toCSS()} {...style}>{value} Left</RenderBox>;
};

export const CardPileVisual: LFC<{ object: CardPile }> = ({ object, children }) => {

    const { beingEdited } = usePose(object);

    const shuffle = usePressable(object, 'shuffle');
    const drawOne = usePressable(object, 'drawOne');
    const drawAll = usePressable(object, 'drawAll');

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder object={object} />
                <RenderStack object={object} />
                <RenderButton area={object.layout.shuffle} interaction={shuffle} label="Shuffle" />
                <RenderButton area={object.layout.drawOne} interaction={drawOne} label="Draw One" />
                <RenderButton area={object.layout.drawAll} interaction={drawAll} label="Draw All" />
                {!beingEdited && <RenderTextField object={object} name="label" />}
                <RenderCount object={object} />
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};
